import { create } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
  membership_role: string;
  is_default: boolean;
}

export interface SlackConnection {
  is_configured: boolean;
  is_connected: boolean;
  team_id?: string | null;
  team_name?: string | null;
  slack_user_id?: string | null;
  slack_user_name?: string | null;
  slack_user_email?: string | null;
  connected_at?: string | null;
}

export interface TeamsConnection {
  is_configured: boolean;
  is_connected: boolean;
  aad_tenant_id?: string | null;
  aad_object_id?: string | null;
  teams_user_id?: string | null;
  upn?: string | null;
  email?: string | null;
  link_source?: string | null;
  has_conversation_reference?: boolean;
  connected_at?: string | null;
}

export interface GoogleChatConnection {
  is_configured: boolean;
  is_connected: boolean;
  google_domain?: string | null;
  customer_id?: string | null;
  google_user_id?: string | null;
  chat_user_name?: string | null;
  email?: string | null;
  display_name?: string | null;
  link_source?: string | null;
  space_name?: string | null;
  connected_at?: string | null;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  is_admin: boolean;
  realm_id?: string;
  permissions?: string[];
  onboarding_complete?: boolean;
  active_tenant_id?: string | null;
  active_tenant?: TenantSummary | null;
  available_tenants?: TenantSummary[];
  slack_connection?: SlackConnection | null;
  teams_connection?: TeamsConnection | null;
  google_chat_connection?: GoogleChatConnection | null;
}

export const isTenantAdminUser = (user: User | null | undefined): boolean => {
  if (!user) {
    return false;
  }

  if (user.is_admin) {
    return true;
  }

  return ['owner', 'admin'].includes(user.active_tenant?.membership_role ?? '');
};

interface AuthState {
  currentUser: User | null;
  activeTenantId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setCurrentUser: (user: User | null) => void;
  setActiveTenantId: (tenantId: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  setAuth: (user: User) => void;
  clearAuth: () => void;
  hasPermission: (permission: string) => boolean;
}

type AuthStore = AuthState & {
  getState: () => AuthState;
};

export const useAuthStore = create<AuthState, [['zustand/persist', Pick<AuthState, 'currentUser' | 'isAuthenticated'>]]>(
  persist(
    (set, get) => ({
      currentUser: null,
      activeTenantId: null,
      isAuthenticated: false,
      isLoading: false,

      setCurrentUser: (user: User | null) =>
        set({
          currentUser: user,
          activeTenantId: user?.active_tenant_id ?? null,
          isAuthenticated: !!user,
        }),

      setActiveTenantId: (tenantId: string | null) => set({ activeTenantId: tenantId }),

      setIsLoading: (loading: boolean) => set({ isLoading: loading }),

      setAuth: (user: User) =>
        set({
          currentUser: user,
          activeTenantId: user.active_tenant_id ?? null,
          isAuthenticated: true,
        }),

      clearAuth: () =>
        set({
          currentUser: null,
          activeTenantId: null,
          isAuthenticated: false,
        }),

      hasPermission: (permission: string): boolean => {
        const state = get();
        if (!state.currentUser) return false;
        if (state.currentUser.is_admin) return true; // Admins have all permissions
        return state.currentUser.permissions?.includes(permission) ?? false;
      },
    }),
    {
      name: 'auth-storage', // localStorage key
      partialize: (state: AuthState) => ({
        currentUser: state.currentUser,
        activeTenantId: state.activeTenantId,
        isAuthenticated: state.isAuthenticated,
      }),
    } as PersistOptions<AuthState, Pick<AuthState, 'currentUser' | 'activeTenantId' | 'isAuthenticated'>>
  )
);
